/**
 * One error type for the whole app.
 *
 * The backend already returns sanitised, actionable messages — it does the work of turning
 * a GitHub 403-with-remaining-0 into "rate limit exceeded, retry in 132 seconds". The
 * frontend's job is to surface that faithfully and add a retry affordance, never to invent
 * its own copy from a status code.
 *
 * What must never reach a user: "AxiosError: Request failed with status code 403".
 */

export type ErrorType =
  | 'VALIDATION_ERROR'
  | 'AUTHENTICATION_ERROR'
  | 'AUTHORIZATION_ERROR'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMIT'
  | 'EXTERNAL_SERVICE_ERROR'
  | 'TIMEOUT'
  | 'POLICY_VIOLATION'
  | 'CONFIRMATION_ERROR'
  | 'INTEGRATION_REQUIRED'
  | 'INSUFFICIENT_CREDITS'
  | 'INTERNAL_ERROR'
  | 'HTTP_ERROR'
  | 'NETWORK_ERROR'

export class ApiError extends Error {
  readonly errorType: ErrorType
  readonly status: number
  readonly requestId: string | null
  readonly retryable: boolean

  constructor(init: {
    errorType: ErrorType
    message: string
    status: number
    requestId?: string | null
    retryable?: boolean
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.errorType = init.errorType
    this.status = init.status
    this.requestId = init.requestId ?? null
    this.retryable = init.retryable ?? false
  }
}

/** Short label for the error badge. The full message carries the detail. */
export function errorTitle(error: ApiError): string {
  switch (error.errorType) {
    case 'AUTHENTICATION_ERROR':
      return 'GitHub credential rejected'
    case 'AUTHORIZATION_ERROR':
      return 'Not permitted'
    case 'NOT_FOUND':
      return 'Not found'
    case 'RATE_LIMIT':
      return 'GitHub rate limit reached'
    case 'TIMEOUT':
      return 'Timed out'
    case 'EXTERNAL_SERVICE_ERROR':
      return 'GitHub is unavailable'
    case 'POLICY_VIOLATION':
      return 'Blocked by policy'
    case 'CONFIRMATION_ERROR':
      return 'Approval no longer valid'
    case 'INTEGRATION_REQUIRED':
      return 'GitHub not connected'
    case 'INSUFFICIENT_CREDITS':
      // Not "error": the account is simply out of credits, which is a state, not a fault.
      return 'Out of credits'
    case 'VALIDATION_ERROR':
      return 'Invalid request'
    case 'NETWORK_ERROR':
      return 'Cannot reach the server'
    default:
      return 'Something went wrong'
  }
}

/** Whether offering a retry button makes sense. Retrying a 404 does not. */
export function isRetryable(error: unknown): boolean {
  return error instanceof ApiError && (error.retryable || error.status >= 500)
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error
  return new ApiError({
    errorType: 'INTERNAL_ERROR',
    message: error instanceof Error ? error.message : 'Unexpected failure.',
    status: 0,
  })
}
